import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/auth';
import Fetching from "./fetching";
import Empty from './empty';
import moment from 'moment';
import XCircleIcon from '@heroicons/react/24/outline/esm/XCircleIcon';
import CheckCircleIcon from '@heroicons/react/24/outline/esm/CheckCircleIcon';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, DocumentPlusIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { getJustificationRequestsCompanyList, updateJustificationRequestStatus, deleteJustificationRequest } from '../services/api_justification_requests';
import { getEmployeesWithActiveContract } from "../services/api_calendar";
import Swal from 'sweetalert2';
import ListBoxPageSize from './listbox_pase_size';
import ComboboxAutocompleteAPI from "./combobox_autocomplete_api";
import OuSelectionPair from './modal_select_OU_to_pair';
import { TrashIcon } from '@heroicons/react/24/outline';
import CalendarMonthSelect from './calendar_month_select';

function formatJustificationEnd(datetimeFrom, datetimeTo) {
  const from = moment(datetimeFrom);
  const to = moment(datetimeTo);

  // Crea una copia di 'from' per evitare di modificarlo
  const fromCopy = moment(from);

  // Verifica se il datetime_to è esattamente a mezzanotte del giorno successivo
  const isNextDayMidnight =
      to.isSame(fromCopy.add(1, 'days').startOf('day'), 'day') && to.format('HH:mm') === '00:00';
  
  // Se datetime_to è a mezzanotte del giorno successivo, mantieni la stessa data di inizio per il "giorno" di fine
  const endDate = isNextDayMidnight ? from.format('DD/MM/YYYY') : to.format('DD/MM/YYYY');
  const endTime = isNextDayMidnight ? '24:00' : to.format('HH:mm');
  
  return {
      date: endDate,
      time: endTime,
  };
}


const GiustificativiRichiesteNoNull = ({ selectedUnit }) => {
    const { token } = useContext(AuthContext);
    const [richieste, setRichieste] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [pageSize, setPageSize] = useState(25);
    const [showUnitSelection, setShowUnitSelection] = useState(false);
    const [selectedJustification, setSelectedJustification] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [calendarVisible, setCalendarVisible] = useState(false);

    const startOfMonth = moment(selectedMonth).startOf('month').format('YYYY-MM-DD');
    const endOfMonth = moment(selectedMonth).endOf('month').format('YYYY-MM-DD');

    const [fromDate, setFromDate] = useState(startOfMonth || '');
    const [toDate, setToDate] = useState(endOfMonth || '');
    const controller = useRef(null);
    

    useEffect(() => {
        const startOfMonth = moment(selectedMonth).startOf('month').format('YYYY-MM-DD');
        const endOfMonth = moment(selectedMonth).endOf('month').format('YYYY-MM-DD');

        setFromDate(startOfMonth);
        setToDate(endOfMonth);

    }, [selectedMonth]);
 
    useEffect(() => {
        if (controller.current) {
            controller.current.abort();
        }

        controller.current = new AbortController();
        const signal = controller.current.signal;
    
        const getData = async () => {
            
            setIsLoading(true);
            setIsError(false);
            
            try {
                const response = await getJustificationRequestsCompanyList(
                    token,
                    fromDate,
                    page,
                    pageSize,
                    'no_null',
                    selectedUnit?.id,
                    selectedEmployees.map((e) => e.id),
                    toDate,
                    signal,
                    'justifications'
                );
        
                if (response && Array.isArray(response.results)) {
                    const filteredRequests = response.results;
                    const totalPageCount = Math.ceil(response.count / pageSize);
                    setRichieste(filteredRequests);
                    setTotalPages(totalPageCount);
                    setIsLoading(false);
                } else {
                    setRichieste([]);
                }
        
            } catch (error) {
                console.log('ERRORE', error);
                if (error?.response?.status === 402) {
                    setIsError(false);
                } else if (error?.name != 'CanceledError') {
                    console.error(error);
                    setIsError(true);
                    setIsLoading(false);
                } 
            }
        };

        getData();

    }, [token, selectedUnit, selectedEmployees, page, pageSize, fromDate, toDate]);

    const handlePreviousMonth = () => {
        setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1));
    };

    const handleSelectClick = () => {
        setCalendarVisible(!calendarVisible);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize); 
        setPage(1); 
    };
    
    const handlePrevPage = () => {
        if (page > 1) setPage(page - 1);
    };
    
    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };       

    const handleSelectEmployee = (selected) => {
        if(selected) {
            setSelectedEmployees([selected]);
        }
        else {
            setSelectedEmployees([])
        }
    };

    const handleResetSelection = () => {
        setSelectedEmployees([]);
    };
 
    const handleUpdateRequest = async (request) => {
        const currentDate = new Date();
        const newStatus = request.status === 'approved' ? 'rejected' : 'approved';
    
        const { isConfirmed } = await Swal.fire({
            title: 'Conferma Modifica',
            html: `
                <div class="mt-2">
                    <p class="text-gray-500">
                        La richiesta del dipendente ${request.employee.last_name ? 
                        `${request.employee.last_name} ${request.employee.first_name}` : 
                        request.employee.cf || ''} è stata ${request.status === 'approved' ? 
                        '<span class="text-green-500">Approvata</span>' : 
                        '<span class="text-red-500">Rifiutata</span>'} 
                        il ${moment(request.reviewed_on).format('DD/MM/YYYY, HH:mm')}
                        ${request.reviewed_by?.first_name || request.reviewed_by?.last_name ? 
                        ` da ${request.reviewed_by.first_name} ${request.reviewed_by.last_name}.` : ''} 
                        <br />Vuoi modificare?
                    </p>
                </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: newStatus === 'approved' ? 'Approva Richiesta' : 'Rifiuta Richiesta',
            cancelButtonText: 'Annulla',
            reverseButtons: true
        });
    
        if (isConfirmed) {
            try {
                await updateJustificationRequestStatus(token, request.id, newStatus, currentDate);
                
                const updatedRequest = {
                    ...request,
                    status: newStatus,
                    reviewed_on: currentDate
                };
    
                const updatedRequests = richieste.map(richiesta =>
                    richiesta.id === updatedRequest.id ? updatedRequest : richiesta
                );
                setRichieste(updatedRequests);
    
                Swal.fire({
                    title: 'Salvato!',
                    text: `Stato della richiesta modificato con successo a "${newStatus === 'approved' ? 'Approvata' : 'Rifiutata'}"`,
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
    
            } catch (error) {
                console.error('Error updating justification request status:', error);
               
                let errorDetails = error.response?.data
                    ? Object.values(error.response.data).flat()
                    : [];
                console.log('Error details:', errorDetails);
                
                const errorMessage =
                    errorDetails.length > 0
                    ? errorDetails.join('\n')
                    : 'Errore nell\'aggiornamento della richiesta.';
                    
                Swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: errorMessage,
                });
            }
        }
    };

    
    const handleDeleteRequest = async (request) => {   
        const { isConfirmed } = await Swal.fire({
            title: 'Conferma Eliminazione',
            html: `
                <div class="mt-2">
                    <p class="text-gray-500">
                        Sei sicuro di voler eliminare la richiesta di giustificativo del dipendente 
                        ${request.employee.last_name ? 
                            `${request.employee.last_name} ${request.employee.first_name}` : 
                            request.employee.cf || ''}?
                    </p>
                </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla',
            reverseButtons: true
        });
    
        if (isConfirmed) {
            let loaderSwal;
    
            try {
                loaderSwal = Swal.fire({
                    title: 'Eliminazione in corso...',
                    text: 'Attendere prego.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                await deleteJustificationRequest(token, request.id);
    
                const updatedRequests = richieste.filter(richiesta => richiesta.id !== request.id);
                setRichieste(updatedRequests);
    
                Swal.close();
                Swal.fire({
                    title: 'Eliminato!',
                    text: 'La richiesta di giustificativo è stata eliminata con successo.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error('Error deleting justification request:', error);
                
                Swal.close();
                Swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: 'Errore durante l\'eliminazione della richiesta.'
                });
            }
        }
    };

    const handleSelectOUtoPair = async (selectedUnitToPair) => {
        if (!selectedJustification || !selectedUnitToPair) {
            console.error('Missing justification or OU ID.');
            return;
        }
        
        try {
            const updatedJustification = await updateJustificationRequestStatus(
                token,
                selectedJustification.id,
                undefined,   // Non inviare lo stato
                undefined,   // Non inviare la data di revisione
                undefined,   // Non inviare datetime_from
                undefined,   // Non inviare datetime_to
                undefined,   // Non inviare note
                selectedUnitToPair.id    // aggiorna solo OU
            );

            const updatedRequest = {
                ...updatedJustification,
                ou: updatedJustification.ou,
                ou_name: updatedJustification.ou_name
            };
            
            const updatedRequests = richieste.map(richiesta =>
                richiesta.id === selectedJustification.id ? {...richiesta, ...updatedJustification} : richiesta
            );

            setRichieste(updatedRequests);
            
            Swal.fire({
                title: 'Salvato!',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error updating justification request:', error);
            
            let errorMessage = 'Si è verificato un errore nell\'aggiornamento.';
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorMessages = Object.values(errorData)
                    .flat()
                    .join(', ');
    
                if (errorMessages) {
                    errorMessage = errorMessages;
                }
            }
            
            Swal.fire({
                title: 'Errore!',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'Chiudi'
            });
        }
    };  

    const handleDeletePairedUnit = async (justificationId) => {
        if (!justificationId) {
            console.error('Justification ID mancante.');
            return;
        }
    
        try {
            const updatedJustification = await updateJustificationRequestStatus(
                token,
                justificationId,
                undefined, // Non modifica lo stato
                undefined, // Non modifica la data di revisione
                undefined, // Non modifica datetime_from
                undefined, // Non modifica datetime_to
                undefined, // Non modifica le note
                null       // Imposta l'unità organizzativa a null
            );
    
            const updatedRequest = {
                ...richieste.find(r => r.id === justificationId),
                ou: null,
            };
    
            const updatedRequests = richieste.map(richiesta =>
                richiesta.id === justificationId ? updatedRequest : richiesta
            );
            setRichieste(updatedRequests);
    
            Swal.fire({
                title: 'Salvato!',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            });
    
        } catch (error) {
            console.error('Errore durante l\'eliminazione dell\'unità associata:', error);
            
            Swal.fire({
                title: 'Errore!',
                text: 'Si è verificato un errore durante l\'eliminazione dell\'unità associata.',
                icon: 'error',
                confirmButtonText: 'Chiudi'
            });
        }
    };


    return (
        <div className="min-h-full">
            <div className="flex justify-end px-4 sm:px-6 lg:px-8 pt-6">
                <div className="col-span-1 flex items-center ml-10">
                    <button
                        className={`w-28 mt-2 sm:mt-0  me-4 inline-flex justify-center align-middle items-center rounded bg-indigo-600 h-6 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${selectedEmployees.length === 0 ? 'invisible' : ''}`}
                        onClick={handleResetSelection}
                    >
                        Reset selezione
                    </button>
                </div>
                <div className="relative w-full sm:w-64">
                    <ComboboxAutocompleteAPI
                        getData={async (query) => {
                        if (query.trim() === '') {
                            return Promise.resolve([]);
                        }

                        const page = 1;
                        const pageSize = 10;

                        try {
                            const res = await getEmployeesWithActiveContract(token, query, 'all', null, page, pageSize);
                            const employeesArray = Array.isArray(res.results) ? res.results : [];
                            return employeesArray;
                        } catch (error) {
                            console.error("Errore durante il recupero dei dati:", error);
                            return [];
                        }
                        }}
                        getDisplay={(employee) => 
                        employee.last_name && employee.first_name 
                            ? `${employee.last_name} ${employee.first_name}` 
                            : employee.name || employee.cf || ''
                        }
                        getId={(employee) => employee.id}
                        onChange={handleSelectEmployee} 
                        currentValue={selectedEmployees[0] || null}
                        placeholder="Digita per cercare il dipendente"
                    />
                </div>
            </div>

            <div className="px-4 sm:px-6 lg:px-8 pt-2">
                <div className='px-3 py-3 w-full bg-white' style={{ position: 'relative', zIndex: '11'}}> 
                    <ListBoxPageSize value={pageSize} onChange={handlePageSizeChange} />
                </div>
                <div className='grid grid-cols-1 w-full pt-4 border border-gray-200 rounded-md'>
                    <div
                        style={{ flex: '1 1 auto', width: 'auto' }}
                        className="pb-5 pt-2 px-4 border-l border-r border-gray-200 text-center w-24 text-xs font-semibold text-gray-600 uppercase align-middle"
                    >
                        <div className='grid  md:grid-cols-5 sm:justify-start md:justify-around grid-cols-3'>
                            <div className='col-span-1 p-2 flex align-center justify-end sm:justify-center md:justify-end me-3'>
                                <button
                                    className='text-gray-700 font-semibold px-1 hover:bg-gray-100'
                                    onClick={handlePreviousMonth} 
                                >
                                    <ChevronLeftIcon className="w-5 h-5 cursor-pointer" />
                                </button>
                            </div>
                            <div className='col-span-1 p-2 flex align-center md:justify-end sm:justify-center me-3'>Seleziona Mese</div>
                            <div className='col-span-1 p-2 align-center justify-start sm:justify-center md:justify-start md:hidden block'>
                                <button
                                    className='text-gray-700 font-semibold px-1 hover:bg-gray-100'
                                    onClick={handleNextMonth}
                                >
                                    <ChevronRightIcon className='w-5 h-5 cursor-pointer' />
                                </button>
                            </div>
                            <div
                                onClick={handleSelectClick}
                                style={{ cursor: 'pointer' }}
                                className='col-span-3 md:col-span-2 border border-gray-300 rounded-md shadow-md p-2 pl-4 flex items-center md:w-4/5 w-full justify-between'
                            >
                                <div>
                                    {selectedMonth.toLocaleDateString('it-IT', { month: 'long' })}, {selectedMonth.getFullYear()}
                                </div>
                                {!calendarVisible ? <ChevronDownIcon className='h-4 w-4 bg-indigo-100 rounded-md' /> : <ChevronUpIcon className='h-4 w-4 bg-indigo-100 rounded-md' />}
                            </div>
                            <div className='col-span-1 p-2 align-start justify-start hidden md:block'>
                                <button
                                    className='text-gray-700 font-semibold px-1 hover:bg-gray-100 flex items-start'
                                    onClick={handleNextMonth}
                                >
                                    <ChevronRightIcon className='w-5 h-5 cursor-pointer' />
                                </button>
                            </div>
                        </div>
                        {calendarVisible &&
                            <CalendarMonthSelect
                                selectedMonth={selectedMonth}
                                onMonthSelect={setSelectedMonth}
                                onCloseCalendar={handleSelectClick}
                            />
                        }
                    </div>
                </div>

                
                {isError && 'Error loading data...'}
                {isLoading && <Fetching />}
                            
                {!isLoading && !isError && richieste.length === 0 ? (
                    <Empty />
                ) : (
                    <div className="-mx-4 mt-3 ring-1 ring-gray-300 mx-1 rounded-lg" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className='sticky top-0 z-10 bg-white'>
                                <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                    Dipendente
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                    Richiesta
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                    Status
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                    
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {richieste.sort((a, b) => a.id - b.id).map((item) => {
                                const { date: endDate, time: endTime } = formatJustificationEnd(item.datetime_from, item.datetime_to);
                                return [
                                    <tr key={item.id} className='border-b border-gray-200'>
                                        <td className="relative py-4 pl-4 pr-3 text-sm sm:pl-6">
                                            <span className="font-medium text-gray-900">
                                                {item.employee.first_name
                                                ? `${item.employee.last_name} ${item.employee.first_name}`
                                                : item.employee.cf}
                                            </span>
                                        </td>
                                        <td className='relative py-4 pl-4 pr-3 text-sm sm:pl-6' style={{ width: '45%' }}>
                                            <span className='underline text-gray-600'>{item.justification.justification?.description}</span>
                                            <br />
                                            {moment(item.datetime_from).format('HH:mm:ss') === '00:00:00' && moment(item.datetime_to).format('HH:mm:ss') === '23:59:59' ? (
                                                <>
                                                    dal <span className='font-semibold text-gray-600'>{item.datetime_from && moment(item.datetime_from).format('DD/MM/YYYY')}</span> 
                                                    {' '}al <span className='font-semibold text-gray-600'>{item.datetime_to && moment(item.datetime_to).format('DD/MM/YYYY')}</span>
                                                </>
                                            ) : (
                                                <>
                                                    dalle ore{' '}
                                                    <span className='font-semibold text-gray-600'>{moment(item.datetime_from).format('HH:mm')}</span> del{' '}
                                                    <span className='font-semibold text-gray-600'>{moment(item.datetime_from).format('DD/MM/YYYY')}</span>
                                                    <br />
                                                    alle ore{' '}
                                                    <span className='font-semibold text-gray-600'>{`${endTime}`}</span> del{' '}
                                                    <span className='font-semibold text-gray-600'>{`${endDate}`}</span>
                                                </>
                                            )}
                                        </td>
                                        <td className="relative py-4 pl-4 pr-3 text-sm sm:pl-6 text-left">
                                            {item.status === 'approved' ? (
                                            <CheckCircleIcon className="w-5 h-5 ml-4 text-teal-500" />
                                            ) : (
                                            <XCircleIcon className="w-5 h-5 ml-4 text-red-500" />
                                            )}
                                        </td>
                                        <td className="relative py-4 pl-4 pr-3 text-sm sm:pl-6 text-right">
                                            <div className="flex flex-col space-y-2">
                                                <button
                                                    onClick={() => handleUpdateRequest(item)}
                                                    className="w-36 text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 p-2 py-1 text-white rounded-md whitespace-nowrap"
                                                >
                                                    Modifica Status
                                                </button>
                                                <button
                                                    className="w-36 text-sm font-semibold bg-white shadow-lg border border-gray-300 hover:bg-red-500 hover:text-white p-2 py-1 text-gray-900 rounded-md whitespace-nowrap"
                                                    onClick={() => handleDeleteRequest(item)}
                                                >
                                                    Elimina
                                                </button>
                                                {!item?.ou && (
                                                    <button
                                                        className="w-36 text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 p-2 py-1 text-white rounded-md whitespace-nowrap"
                                                        onClick={() => {
                                                            setSelectedJustification(item);
                                                            setShowUnitSelection(true);
                                                        }}
                                                    >
                                                        Associa Unità
                                                    </button>
                                                )}
                                            </div>
                                        </td> 
                                    </tr>,
                                    (
                                    item.note && item.note.length > 0 && (
                                        <tr key={`${item.id}-note`} className="border-b border-gray-200 bg-indigo-50">
                                        <td colSpan="6" className="py-3 px-6 text-gray-600 text-sm text-left"><span className='text-gray-700 font-semibold'>Note:</span> {item.note}</td>
                                        </tr>
                                    )),
                                    (
                                    item?.ou && (
                                        <tr key={`${item.id}-ou`} className="border-b border-gray-400 bg-gray-50">
                                            <td colSpan="6" className="py-3 px-6 text-gray-600 text-sm text-left">
                                                <span className='text-gray-700 font-semibold'>Unità Associata:</span> {item.ou_name}
                                                <button className="text-gray-700 font-bold px-1" title="Elimina" onClick={() => handleDeletePairedUnit(item.id)}>
                                                    <TrashIcon className="h-5 w-5 inline-block hover:text-red-500" /> 
                                                </button>
                                            </td>
                                        </tr>
                                    )),
                                ]})}
                            </tbody>
                        </table>
                    </div>
                )}
                {totalPages > 1 && (
                    <div className="flex justify-between mt-8">
                        <button 
                            className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            onClick={handlePrevPage} 
                            disabled={page === 1}
                        >
                            Precedente
                        </button>
                        <span className='text-sm font-medium text-gray-900'>Pagina {page} di {totalPages}</span>
                        <button 
                            className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            onClick={handleNextPage} 
                            disabled={page === totalPages}
                        >
                            Successivo
                        </button>
                    </div>
                )}
            </div>

            {showUnitSelection && (
                <OuSelectionPair
                    onClose={() => setShowUnitSelection(false)} 
                    justification={selectedJustification}
                    onSelectOUtoPair={handleSelectOUtoPair}
                />
            )}
        </div>
    );
};

export default GiustificativiRichiesteNoNull;